import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { BEFORE_SALE, COMPLETED, ORIGIN_DATA_REGISTER, PAUSE, SALE, WAIT } from '../../const/productStatus';
import ConfirmModal from '../Common/Modal/ConfirmModal';
import BottomSheetPopup from '../Common/Popup/BottomSheetPopup';
import { EllipsisVerticalIcon } from '../Icon';
import ActualPriceModal from './ActualPriceModal';
import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { patchProductSalePrice } from '@/api/products/updateProducts';
import DiscontinuedModal, { DiscontinuedConfirmPayload } from '@/components/Products/DiscontinuedModal';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { formatPrice } from '@/utils/common';
import { canModifyProduct } from '@/utils/Products/productUtils';

interface MenuDataType {
  code: string;
  desc: string;
  onClick?: () => void;
}

export interface MoreMenuData {
  id: number;
  status: string;
  productsType?: string;
}

export interface MoreMenuProps {
  id: number;
  status?: string;
  productsType?: string;
  setProductList?: React.Dispatch<React.SetStateAction<ProductDetailResponse[]>>;
  setLicenseList?: React.Dispatch<React.SetStateAction<License[]>>;
  refetch?: () => void;
  isLicense?: boolean;
  confirmTitle?: string;
  price?: number;
}

export default function ProductMoreMenu({
  id,
  status,
  productsType,
  setProductList,
  refetch,
  isLicense = false,
  confirmTitle,
  price,
}: MoreMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState(false);

  const [actualSalePrice, setActualSalePrice] = useState<number>();
  const [menus, setMenus] = useState<MenuDataType[]>([]);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [path, setPath] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState(status || null);
  const { showToast } = useToastContext();
  const [isUsePriceInputPopup, setIsUsePriceInputPopup] = useState(false);
  const [isOpenDiscontinuedModal, setIsOpenDiscontinuedModal] = useState(false);
  const completeReasonRef = useRef<string>('');

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: '',
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  useEffect(() => {
    if (isLicense) {
      setPath('license');
    } else {
      setPath('products');
    }
  }, []);

  useEffect(() => {
    if (status) {
      setSelectedStatus(status);
      // 상태가 변경되면 메뉴도 업데이트
      if (isOpenBottomSheet) {
        setMenuData(isLicense);
      }
    }
  }, [status, isOpenBottomSheet, isLicense, productsType]);

  const setMenuData = (isLicense: boolean) => {
    const menus: { code: string; desc: string }[] = [];
    const canModify = isLicense || canModifyProduct(productsType);

    const addMenu = (code: string, desc: string, condition: boolean = true) => {
      if (condition) menus.push({ code, desc });
    };

    switch (selectedStatus) {
      case ORIGIN_DATA_REGISTER:
      case BEFORE_SALE:
        addMenu('MODIFY', '수정하기', canModify);
        addMenu('DELETE', '삭제하기', isLicense);
        break;
      case SALE:
        addMenu('COMPLETED', '판매완료', isLicense);
        addMenu('PAUSE', '판매중지', isLicense);
        addMenu('MODIFY', '수정하기', canModify);
        addMenu('LICENSE', '번호판 관리', !isLicense);
        addMenu('DELETE', '삭제하기', isLicense);
        break;
      case PAUSE:
        addMenu('SALE', '판매중', isLicense);
        addMenu('MODIFY', '수정하기', isLicense);
        addMenu('LICENSE', '번호판 관리', !isLicense);
        addMenu('DELETE', '삭제하기', isLicense);
        break;
      case COMPLETED:
        addMenu('LICENSE', '번호판 관리', !isLicense);
        addMenu('DELETE', '삭제하기', isLicense);
        break;
      case WAIT:
        // 판매대기(WAIT)는 상태값 변경은 막고 수정하기/번호판 관리만 노출한다.
        addMenu('MODIFY', '수정하기', canModify);
        addMenu('LICENSE', '번호판 관리', !isLicense);
        break;
    }
    setMenus(menus);
  };

  const onClickMenu = (isLicense: boolean) => {
    setIsOpenBottomSheet(true);
    setMenuData(isLicense);
  };

  const closeConfrimModal = () => {
    setIsOpenConfirmModal(false);
  };

  const closeBottomSheet = () => {
    setIsOpenBottomSheet(false);
  };

  const onClickDelete = () => {
    setTimeout(() => {
      setIsOpenConfirmModal(true);
    }, 100);

    setModalConfig({
      title: confirmTitle,
      content: `${isLicense ? '번호판을 정말 삭제하시겠어요?' : '차량을 정말 삭제하시겠어요?'}`,
      rightButtonLabel: '삭제하기',
      onLeftButtonClick: closeConfrimModal,
      onRightButtonClick: () => {
        setIsOpenBottomSheet(false);
        closeBottomSheet();
        closeConfrimModal();
        deleteProduct();
      },
    });
  };

  const deleteProduct = () => {
    apiManager
      .delete(`/api/v1/${path}/${id}`)
      .then((response) => {
        if (!isLicense) {
          showToast('차량이 삭제되었어요.', 'success', 'bottom');
          if (setProductList) {
            setTimeout(() => {
              setProductList((prev) => prev.filter((item) => item.id !== id));
            }, 500);
          }
          const isVehicleDetailPage = /^\/products\/\d+$/.test(location.pathname);
          if (isVehicleDetailPage) {
            navigate('/products', { replace: true });
            queryClient.invalidateQueries({ queryKey: ['product-search'] });
          }
        } else if (refetch) {
          showToast('번호판이 삭제되었어요.', 'success', 'bottom');
          refetch();
        }
      })
      .catch((error) => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });
  };

  const handleToastMsg = (selectedStatus: string, isLicense: boolean) => {
    switch (selectedStatus) {
      case SALE:
        return '판매중으로 변경되었어요.';
      case PAUSE:
        return '판매중지로 변경되었어요.';
      case COMPLETED:
        return isLicense && '판매완료로 변경되었어요.';
      default:
        return '';
    }
  };

  const showActualSalePricePopup = () => {
    setIsOpenConfirmModal(false);
    setIsUsePriceInputPopup(true);
  };

  const onClickChangeStatus = (selectedStatus: string) => {
    if (path) {
      newApiManager
        .patch(`/api/v1/${path}/${id}`, { status: selectedStatus })
        .then((response) => {
          if (!isLicense) {
            const responseData: ProductDetailResponse = response.data;
            if (setProductList) {
              setProductList((prevData) =>
                prevData.map((item) => {
                  if (item.id === responseData.id) {
                    return { ...item, status: responseData.status };
                  }
                  return item;
                }),
              );
            }
            if (responseData && responseData.status.code == COMPLETED) {
              showActualSalePricePopup();
            }
          } else if (refetch) {
            setIsOpenBottomSheet(false);
            closeConfrimModal();
            refetch();
          }
          setSelectedStatus(selectedStatus);
          const newToastMsg = handleToastMsg(selectedStatus, isLicense);
          if (!!newToastMsg) {
            showToast(newToastMsg, 'success', 'bottom');
          }
        })
        .catch((error) => {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        });
    }
  };

  const onClickMenuItem = (code: string) => {
    switch (code) {
      case 'MODIFY':
        if (isLicense) {
          navigate(`/${path}/form/${id}`, { state: { isModify: true } });
        } else {
          navigate(`/${path}/sales/edit/${id}/#info`);
        }
        break;
      case 'DELETE':
        onClickDelete();
        break;
      case 'COMPLETED':
        onClickChangeStatus(code);
        break;
      case 'PAUSE':
        if (!isLicense) {
          setIsOpenBottomSheet(false);
          setIsOpenDiscontinuedModal(true);
        } else {
          onClickChangeStatus(code);
          setIsOpenBottomSheet(false);
        }
        break;
      case 'SALE':
        onClickChangeStatus(code);
        setIsOpenBottomSheet(false);
        break;
      case 'LICENSE':
        navigate('/license/my');
        break;
    }
  };

  const patchActualPriceMutation = useMutation(
    (request: { id: number; actualSalePrice: number; completeReason: string }) => patchProductSalePrice(request),
    {
      onSuccess: (response) => {
        const responseData: ProductDetailResponse = response.data;
        if (responseData) {
          showToast('실제 판매 가격이 저장되었어요.', 'success', 'bottom');
          if (setProductList) {
            setTimeout(() => {
              setProductList((prevData) =>
                prevData.map((item) => {
                  if (item.id === responseData.id) {
                    return { ...item, actualSalePrice: responseData.actualSalePrice };
                  }
                  return item;
                }),
              );
            }, 1000);
          }
          setIsOpenBottomSheet(false);
        }
      },
      onError: () => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      },
    },
  );

  const modifyProductActualSalePrice = () => {
    if (price && actualSalePrice) {
      if (price + 1000 < actualSalePrice || price - 1000 > actualSalePrice) {
        setIsOpenConfirmModal(true);

        setModalConfig({
          content: (
            <>
              <div className="mx-8 font-medium sm280:mx-0">
                입력하신 실제 판매 금액과
                <br />
                기존 금액의 차이가 큽니다.
                <br />
                이대로 입력하시겠어요?
              </div>
              <div className="text-gray-7 text-[16px] sm280:text-[14px] leading-[19px] sm280:leading-[17px] mt-[24px]">
                <div>기존 금액 {formatPrice(String(price))}</div>
                <div>실제 판매 금액 {formatPrice(String(actualSalePrice))}</div>
              </div>
            </>
          ),
          leftButtonLabel: '다시 입력',
          rightButtonLabel: '이대로 입력',
          onLeftButtonClick: () => {
            closeConfrimModal();
            setIsUsePriceInputPopup(true);
          },
          onRightButtonClick: () => {
            updateActualSalePrice();
          },
        });
      } else {
        updateActualSalePrice();
      }
    }
  };

  const updateActualSalePrice = () => {
    if (actualSalePrice) {
      setIsOpenBottomSheet(false);
      closeConfrimModal();

      patchActualPriceMutation.mutate({
        id: id,
        actualSalePrice: actualSalePrice,
        completeReason: completeReasonRef.current,
      });
    }
  };

  return (
    <>
      <button onClick={() => onClickMenu(isLicense)}>
        <EllipsisVerticalIcon />
      </button>

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        title={modalConfig.title}
        content={modalConfig.content}
        leftButtonLabel={modalConfig.leftButtonLabel}
        rightButtonLabel={modalConfig.rightButtonLabel}
        onLeftButtonClick={modalConfig.onLeftButtonClick}
        onRightButtonClick={modalConfig.onRightButtonClick}
      />
      <ActualPriceModal
        isShow={isUsePriceInputPopup}
        setIsShow={setIsUsePriceInputPopup}
        placeholder="판매 금액"
        price={price}
        onClickOk={(completeReason) => {
          completeReasonRef.current = completeReason;
          modifyProductActualSalePrice();
        }}
        inputValue={actualSalePrice}
        setInputValue={setActualSalePrice}
      />
      <DiscontinuedModal
        isOpen={isOpenDiscontinuedModal}
        onClose={() => setIsOpenDiscontinuedModal(false)}
        onConfirm={({ pauseReason }: DiscontinuedConfirmPayload) => {
          newApiManager
            .patch(`/api/v1/products/${id}`, { pauseReason, status: PAUSE })
            .then((response) => {
              const responseData: ProductDetailResponse = response.data;
              if (setProductList && responseData) {
                setProductList((prevData) =>
                  prevData.map((item) =>
                    item.id === responseData.id ? { ...item, status: responseData.status } : item,
                  ),
                );
              }
              setSelectedStatus(PAUSE);
              showToast('판매중지로 변경되었어요.', 'success', 'bottom');
              setIsOpenDiscontinuedModal(false);
            })
            .catch(() => {
              showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
            });
        }}
      />

      {/* z-index가 있는 부모(내차관리 카드 등) 안에 렌더링되면 하단 탭바 뒤로 가려지므로 body에 렌더링한다. */}
      {createPortal(
        <BottomSheetPopup
          isOpen={isOpenBottomSheet}
          onClose={closeBottomSheet}
          canClose={() => !isUsePriceInputPopup && !isOpenConfirmModal}
          title="메뉴"
        >
          <div className="p-4">
            {menus?.map((item, index) => (
              <div
                key={index}
                className={`py-4 cursor-pointer ${item.code === 'DELETE' && 'text-red'}`}
                onClick={() => onClickMenuItem(item.code)}
              >
                {item.desc}
              </div>
            ))}
          </div>
        </BottomSheetPopup>,
        document.body,
      )}
    </>
  );
}
